class CalculateValueService {
  static parseNumericValue(rawValue) {
    if (rawValue === undefined || rawValue === null || rawValue === '') return 0;
    if (typeof rawValue === 'number') return Number.isFinite(rawValue) ? rawValue : 0;
    if (typeof rawValue === 'boolean') return rawValue ? 1 : 0;

    let normalized = `${rawValue}`.trim().replace(/\s/g, '');
    if (normalized === '') return 0;

    if (normalized.indexOf(',') > -1 && normalized.indexOf('.') > -1) {
      if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = normalized.replace(/,/g, '');
      }
    } else if (normalized.indexOf(',') > -1) {
      normalized = normalized.replace(',', '.');
    }

    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  static normalizePrecision(decimals) {
    let precision = decimals === undefined ? 0 : parseInt(decimals, 10);
    precision = Number.isNaN(precision) ? 0 : Math.max(precision, 0);
    return precision;
  }

  static roundValue(value, decimals) {
    return Number(
      CalculateValueService.parseNumericValue(value).toFixed(
        CalculateValueService.normalizePrecision(decimals)
      )
    );
  }

  static roundUpValue(value, decimals) {
    const numericValue = CalculateValueService.parseNumericValue(value);
    const factor = Math.pow(10, CalculateValueService.normalizePrecision(decimals));
    return Math.ceil(numericValue * factor) / factor;
  }

  static roundDownValue(value, decimals) {
    const numericValue = CalculateValueService.parseNumericValue(value);
    const factor = Math.pow(10, CalculateValueService.normalizePrecision(decimals));
    return Math.floor(numericValue * factor) / factor;
  }

  static normalizeFieldReference(reference) {
    let fieldReference = (reference || '').trim();

    if (/^person\.fields\./i.test(fieldReference)) {
      fieldReference = fieldReference.replace(/^person\.fields\./i, '');
    } else if (/^person\.extra\./i.test(fieldReference)) {
      fieldReference = fieldReference.replace(/^person\.extra\./i, '');
    } else if (/^person\./i.test(fieldReference)) {
      fieldReference = fieldReference.replace(/^person\./i, '');
    }

    return fieldReference;
  }

  static resolveFieldNumericValue(reference, values) {
    const sampleValues = values || {};
    const fieldReference = CalculateValueService.normalizeFieldReference(reference);

    if (fieldReference === '') return 0;

    if (
      sampleValues[fieldReference] !== undefined &&
      sampleValues[fieldReference] !== null &&
      sampleValues[fieldReference] !== ''
    ) {
      return CalculateValueService.parseNumericValue(sampleValues[fieldReference]);
    }

    if (
      sampleValues[reference] !== undefined &&
      sampleValues[reference] !== null &&
      sampleValues[reference] !== ''
    ) {
      return CalculateValueService.parseNumericValue(sampleValues[reference]);
    }

    return 0;
  }

  static resolvePersonFieldNumericValue(reference, person) {
    const currentPerson = person || {};
    const extra = currentPerson.extra || {};
    const fieldReference = CalculateValueService.normalizeFieldReference(reference);

    if (fieldReference === '') return 0;

    if (
      extra[fieldReference] !== undefined &&
      extra[fieldReference] !== null &&
      extra[fieldReference] !== ''
    ) {
      return CalculateValueService.parseNumericValue(extra[fieldReference]);
    }

    if (
      currentPerson[fieldReference] !== undefined &&
      currentPerson[fieldReference] !== null &&
      currentPerson[fieldReference] !== ''
    ) {
      return CalculateValueService.parseNumericValue(currentPerson[fieldReference]);
    }

    return 0;
  }

  static tokenizeExpression(expression) {
    const tokens = [];
    let index = 0;

    while (index < expression.length) {
      const char = expression[index];
      const nextTwo = expression.substring(index, index + 2);

      if (/\s/.test(char)) {
        index += 1;
        continue;
      }

      if (['>=', '<=', '==', '!=', '&&', '||'].includes(nextTwo)) {
        tokens.push({ type: 'operator', value: nextTwo });
        index += 2;
        continue;
      }

      if (char === '(') {
        tokens.push({ type: 'lparen', value: char });
        index += 1;
        continue;
      }

      if (char === ')') {
        tokens.push({ type: 'rparen', value: char });
        index += 1;
        continue;
      }

      if (char === ',') {
        tokens.push({ type: 'comma', value: char });
        index += 1;
        continue;
      }

      if (['+', '-', '*', '/', '%', '!', '>', '<'].includes(char)) {
        tokens.push({ type: 'operator', value: char });
        index += 1;
        continue;
      }

      if (
        (char >= '0' && char <= '9') ||
        (char === '.' && expression[index + 1] >= '0' && expression[index + 1] <= '9')
      ) {
        const numberStart = index;
        let hasDot = false;

        if (char === '.') {
          hasDot = true;
          index += 1;
        }

        while (index < expression.length) {
          const current = expression[index];

          if (current >= '0' && current <= '9') {
            index += 1;
            continue;
          }

          if (current === '.' && !hasDot) {
            hasDot = true;
            index += 1;
            continue;
          }

          break;
        }

        tokens.push({
          type: 'number',
          value: parseFloat(expression.substring(numberStart, index)),
        });
        continue;
      }

      if (/[A-Za-z_]/.test(char)) {
        const start = index;
        index += 1;

        while (index < expression.length && /[A-Za-z0-9_]/.test(expression[index])) {
          index += 1;
        }

        const word = expression.substring(start, index).toLowerCase();
        if (word === 'and') tokens.push({ type: 'operator', value: '&&' });
        else if (word === 'or') tokens.push({ type: 'operator', value: '||' });
        else if (word === 'not') tokens.push({ type: 'operator', value: '!' });
        else if (word === 'true') tokens.push({ type: 'number', value: 1 });
        else if (word === 'false') tokens.push({ type: 'number', value: 0 });
        else tokens.push({ type: 'identifier', value: word });
        continue;
      }

      throw new Error(`Invalid character "${char}" in formula.`);
    }

    return tokens;
  }

  static parseTokenExpression(tokens) {
    let index = 0;
    const current = () => tokens[index];

    const consume = (expectedType, expectedValue) => {
      const token = current();

      if (
        !token ||
        token.type !== expectedType ||
        (expectedValue !== undefined && token.value !== expectedValue)
      ) {
        throw new Error('Invalid formula syntax.');
      }

      index += 1;
      return token;
    };

    const applyFunction = (name, args) => {
      if (name === 'if') {
        if (args.length !== 3) throw new Error('if() expects 3 arguments.');
        return args[0] ? args[1] : args[2];
      }

      if (name === 'round') {
        if (args.length < 1 || args.length > 2) {
          throw new Error('round() expects 1 or 2 arguments.');
        }
        return CalculateValueService.roundValue(args[0], args[1]);
      }

      if (name === 'roundup') {
        if (args.length < 1 || args.length > 2) {
          throw new Error('roundup() expects 1 or 2 arguments.');
        }
        return CalculateValueService.roundUpValue(args[0], args[1]);
      }

      if (name === 'rounddown') {
        if (args.length < 1 || args.length > 2) {
          throw new Error('rounddown() expects 1 or 2 arguments.');
        }
        return CalculateValueService.roundDownValue(args[0], args[1]);
      }

      if (name === 'min') {
        if (args.length < 1) throw new Error('min() expects at least 1 argument.');
        return Math.min(...args);
      }

      if (name === 'max') {
        if (args.length < 1) throw new Error('max() expects at least 1 argument.');
        return Math.max(...args);
      }

      throw new Error(`Unsupported function "${name}".`);
    };

    const parseExpression = () => parseOr();

    const parsePrimary = () => {
      const token = current();
      if (!token) throw new Error('Unexpected end of formula.');

      if (token.type === 'number') {
        consume('number');
        return token.value;
      }

      if (token.type === 'identifier') {
        const functionName = token.value;
        consume('identifier');
        consume('lparen');

        const args = [];
        if (!current() || current().type !== 'rparen') {
          args.push(parseExpression());

          while (current() && current().type === 'comma') {
            consume('comma');
            args.push(parseExpression());
          }
        }

        consume('rparen');
        return applyFunction(functionName, args);
      }

      if (token.type === 'lparen') {
        consume('lparen');
        const value = parseExpression();
        consume('rparen');
        return value;
      }

      throw new Error(`Unexpected token "${token.value}".`);
    };

    const parseUnary = () => {
      const token = current();

      if (token && token.type === 'operator' && token.value === '!') {
        consume('operator', '!');
        return parseUnary() ? 0 : 1;
      }

      if (token && token.type === 'operator' && token.value === '-') {
        consume('operator', '-');
        return -parseUnary();
      }

      if (token && token.type === 'operator' && token.value === '+') {
        consume('operator', '+');
        return parseUnary();
      }

      return parsePrimary();
    };

    const parseFactor = () => {
      let left = parseUnary();

      while (
        current() &&
        current().type === 'operator' &&
        ['*', '/', '%'].includes(current().value)
      ) {
        const operator = consume('operator').value;
        const right = parseUnary();

        if (operator === '*') left = left * right;
        else if (operator === '/') {
          if (right === 0) throw new Error('Division by zero is not allowed.');
          left = left / right;
        } else if (operator === '%') {
          if (right === 0) throw new Error('Modulo by zero is not allowed.');
          left = left % right;
        }
      }

      return left;
    };

    const parseTerm = () => {
      let left = parseFactor();

      while (
        current() &&
        current().type === 'operator' &&
        ['+', '-'].includes(current().value)
      ) {
        const operator = consume('operator').value;
        left = operator === '+' ? left + parseFactor() : left - parseFactor();
      }

      return left;
    };

    const parseComparison = () => {
      let left = parseTerm();

      while (
        current() &&
        current().type === 'operator' &&
        ['>', '>=', '<', '<='].includes(current().value)
      ) {
        const operator = consume('operator').value;
        const right = parseTerm();

        if (operator === '>') left = left > right ? 1 : 0;
        else if (operator === '>=') left = left >= right ? 1 : 0;
        else if (operator === '<') left = left < right ? 1 : 0;
        else if (operator === '<=') left = left <= right ? 1 : 0;
      }

      return left;
    };

    const parseEquality = () => {
      let left = parseComparison();

      while (
        current() &&
        current().type === 'operator' &&
        ['==', '!='].includes(current().value)
      ) {
        const operator = consume('operator').value;
        const right = parseComparison();
        left = operator === '==' ? (left == right ? 1 : 0) : left != right ? 1 : 0;
      }

      return left;
    };

    const parseAnd = () => {
      let left = parseEquality();

      while (current() && current().type === 'operator' && current().value === '&&') {
        consume('operator', '&&');
        const right = parseEquality();
        left = left && right ? 1 : 0;
      }

      return left;
    };

    const parseOr = () => {
      let left = parseAnd();

      while (current() && current().type === 'operator' && current().value === '||') {
        consume('operator', '||');
        const right = parseAnd();
        left = left || right ? 1 : 0;
      }

      return left;
    };

    const result = parseExpression();

    if (index < tokens.length) {
      throw new Error(`Unexpected token "${tokens[index].value}" in formula.`);
    }

    return result;
  }

  static evaluateFormula(formulaString, values = {}, options = {}) {
    const source = (formulaString || '').trim();
    if (!source) return { success: false, error: 'Formula is empty.' };

    try {
      const valueResolver =
        options.valueResolver ||
        ((fieldReference) =>
          CalculateValueService.resolveFieldNumericValue(fieldReference, values));

      const resolvedExpression = source.replace(/\{([^{}]+)\}/g, (_, fieldReference) =>
        valueResolver(fieldReference).toString()
      );

      const tokens = CalculateValueService.tokenizeExpression(resolvedExpression);
      let result = CalculateValueService.parseTokenExpression(tokens);

      if (
        typeof result === 'number' &&
        Number.isFinite(result) &&
        options.roundTo !== undefined &&
        options.roundTo !== null &&
        `${options.roundTo}`.trim() !== ''
      ) {
        result = CalculateValueService.roundValue(result, options.roundTo);
      }

      return { success: true, result };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  static fieldReferences(formulaString) {
    const references = new Set();
    const regex = /\{([^{}]+)\}/g;
    const source = formulaString || '';
    let match = regex.exec(source);

    while (match) {
      const reference = (match[1] || '').trim();
      if (reference) references.add(reference);
      match = regex.exec(source);
    }

    return Array.from(references);
  }

  static evaluateAction(action, options = {}) {
    if (!action || action.action_type !== 'Calculate score' || !action.data) {
      return { success: false, error: 'Invalid calculate score action.' };
    }

    const formula = action.data.formula || action.data['formula'];
    const targetField = action.data.field || action.data['field'];
    const roundTo = action.data.round_to || action.data['round_to'];

    if (!(formula && targetField)) {
      return { success: false, error: 'Formula and target field are required.' };
    }

    const person = options.person || {};
    const output = CalculateValueService.evaluateFormula(formula, {}, {
      roundTo,
      valueResolver: (fieldReference) =>
        CalculateValueService.resolvePersonFieldNumericValue(fieldReference, person),
    });

    if (!output.success) return output;

    return {
      success: true,
      field: targetField,
      result: output.result,
      value: output.result.toString(),
    };
  }

  static collectActionsForElement(element, options = {}) {
    if (!element) return {};

    const actionsToRun = {};
    const addAction = (action, key) => {
      if (action && action.action_type === 'Calculate score') {
        actionsToRun[
          key || action.id || `action-${Object.keys(actionsToRun).length}`
        ] = action;
      }
    };

    Object.keys(element.actions || {}).forEach((actionId) => {
      addAction(element.actions[actionId], actionId);
    });

    Object.keys(options.websiteActions || {}).forEach((actionId) => {
      const action = options.websiteActions[actionId];

      if (
        action &&
        action.contactable_type &&
        action.contactable_type.toLowerCase() === 'element' &&
        action.contactable_id == element.id
      ) {
        addAction(action, actionId);
      }
    });

    Object.values(element.action_groups || options.actionGroups || {}).forEach((actionGroup) => {
      const matches =
        typeof options.actionGroupMatches === 'function'
          ? options.actionGroupMatches(actionGroup)
          : false;

      if (!actionGroup.field_id && matches && actionGroup.actions) {
        Object.keys(actionGroup.actions).forEach((actionId) => {
          addAction(actionGroup.actions[actionId], actionId);
        });
      }
    });

    return actionsToRun;
  }
}

if (typeof globalThis !== 'undefined') {
  globalThis.CalculateValueService = CalculateValueService;
}

if (typeof window !== 'undefined' && typeof window.convertflow == 'undefined') {
  window.CalculateValueService = CalculateValueService;
}

export default CalculateValueService;
